import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { getLoginUrl } from "./const";
import "./index.css";
import { getIOSMode } from "@/lib/iosDetection";

// Log iOS mode on startup
const iosMode = getIOSMode();
if (iosMode !== 'not-ios') {
  console.log('[App Startup] iOS Mode:', iosMode);
  console.log('[App Startup] User Agent:', navigator.userAgent);
  console.log('[App Startup] Standalone:', (window.navigator as any).standalone);
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // iOS Safari browser mode needs stricter error handling
      retry: iosMode === 'browser' ? 1 : 3,
      staleTime: iosMode === 'browser' ? 10000 : 30000,
      // Prevent background refetches that might cause issues on iOS
      refetchOnWindowFocus: iosMode !== 'browser',
    },
  },
});

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Add global error handler for iOS debugging
if (iosMode === 'browser') {
  window.addEventListener('error', (event) => {
    console.error('[iOS Browser Mode] Global Error:', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
    });
  });
  
  window.addEventListener('unhandledrejection', (event) => {
    console.error('[iOS Browser Mode] Unhandled Promise Rejection:', {
      reason: event.reason,
      promise: event.promise,
    });
  });
}

createRoot(document.getElementById("root")!).render(
  <trpc.Provider client={trpcClient} queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </trpc.Provider>
);
